import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Badge, Button, Alert, Spinner, Row, Col, Label, Input, Table, FormGroup } from "reactstrap";
import { toast } from "react-toastify";
import moment from "moment";
import {
  companiesHouseFilingSettingsDataApi,
  companiesHouseFilingSettingsSaveApi,
  companiesHouseFilingSettingsCheckApi,
  companiesHouseFilingPreviewApi,
  companiesHouseFilingSubmitApi,
  companiesHouseFilingSubmissionListApi,
  companiesHouseFilingSubmissionDataApi,
  companiesHouseFilingSubmissionCheckStatusApi,
} from "../../../../helpers/fakebackend_helper";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import AppModal from "../../../../Components/Common/AppModal";

// Change #100691 - Companies House accounts filing (XML Gateway, CRMit as software presenter).
//
// Filing details are per company (number, name, type, 6-character authentication code, contact).
// Micro-entity accounts are previewed as the exact iXBRL that will be sent, then submitted to the
// TEST service by default. Live needs the server live-enabled plus the company number typed in,
// because a live filing goes straight onto the public register and Companies House forbids testing there.
// The accept/reject decision comes back later - "Check status" asks the gateway (GetSubmissionStatus).

const COMPANY_TYPE_LABELS = {
  EW: "England & Wales",
  SC: "Scotland",
  WA: "Wales",
  NI: "Northern Ireland",
};

const STATUS_COLORS = {
  SUBMITTED: "info",
  PENDING: "info",
  PARKED: "warning",
  ACCEPT: "success",
  REJECT: "danger",
  ERROR: "danger",
  INTERNAL_FAILURE: "danger",
};

const STATUS_LABELS = {
  SUBMITTED: "Submitted",
  PENDING: "Pending",
  PARKED: "Parked",
  ACCEPT: "Accepted",
  REJECT: "Rejected",
  ERROR: "Error",
  INTERNAL_FAILURE: "CH failure",
};

const FINAL_STATUSES = ["ACCEPT", "REJECT", "ERROR"];

const emptyYear = () => ({
  CalledUpShareCapitalNotPaid: "",
  FixedAssets: "",
  CurrentAssets: "",
  CreditorsWithinOneYear: "",
  CreditorsAfterOneYear: "",
  Equity: "",
  AverageNumberEmployees: "",
});

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const netAssets = (y) =>
  num(y.FixedAssets) +
  num(y.CurrentAssets) -
  num(y.CreditorsWithinOneYear) -
  num(y.CreditorsAfterOneYear) +
  num(y.CalledUpShareCapitalNotPaid);

const yearPayload = (y) => ({
  BalanceSheet: {
    CalledUpShareCapitalNotPaid: num(y.CalledUpShareCapitalNotPaid),
    FixedAssets: num(y.FixedAssets),
    CurrentAssets: { Total: num(y.CurrentAssets) },
    Liabilities: {
      CreditorsWithinOneYear: num(y.CreditorsWithinOneYear),
      CreditorsAfterOneYear: num(y.CreditorsAfterOneYear),
    },
    NetAssets: netAssets(y),
    Equity: { Total: num(y.Equity) },
  },
  AverageNumberEmployees: num(y.AverageNumberEmployees),
});

const BALANCE_SHEET_LINES = [
  ["CalledUpShareCapitalNotPaid", "Called up share capital not paid"],
  ["FixedAssets", "Fixed assets"],
  ["CurrentAssets", "Current assets"],
  ["CreditorsWithinOneYear", "Creditors: amounts falling due within one year"],
  ["CreditorsAfterOneYear", "Creditors: amounts falling due after more than one year"],
];

const fmtDate = (d, f = "DD MMM YYYY") => (d ? moment(d).format(f) : "-");

export default function CompaniesHouseIntegration() {
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null); // environment flags + auth_code_set + company_types
  const [settings, setSettings] = useState({
    company_number: "",
    company_name: "",
    company_type: "EW",
    contact_name: "",
    contact_number: "",
    contact_email: "",
  });
  const [authCode, setAuthCode] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null); // { checks: [...], suggest: {...} }

  const [submissions, setSubmissions] = useState([]);
  const [checkingId, setCheckingId] = useState(null);

  const [fileOpen, setFileOpen] = useState(false);
  const [resubmitFrom, setResubmitFrom] = useState(null); // a past submission's Accounts (#100745)
  const [resubmittingId, setResubmittingId] = useState(null);
  const [viewing, setViewing] = useState(null); // submission data

  const loadSettings = useCallback(async () => {
    try {
      const response = await companiesHouseFilingSettingsDataApi();
      if (response?.status === 200) {
        setMeta(response);
        setSettings((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(response.settings || {}).map(([k, v]) => [k, v ?? ""])
          ),
          company_type: response.settings?.company_type || "EW",
        }));
      } else if (response?.message) {
        toast.error(response.message);
      }
    } catch (e) {
      toast.error("Could not load Companies House filing details.");
    }
  }, []);

  const loadSubmissions = useCallback(async () => {
    try {
      const response = await companiesHouseFilingSubmissionListApi();
      if (response?.status === 200) setSubmissions(response.companieshousesubmission_list || []);
    } catch (e) {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([loadSettings(), loadSubmissions()]);
      setLoading(false);
    })();
  }, [loadSettings, loadSubmissions]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await companiesHouseFilingSettingsSaveApi([
        {
          CompanyNumber: settings.company_number,
          CompanyName: settings.company_name,
          CompanyType: settings.company_type,
          AuthCode: authCode,
          ContactName: settings.contact_name,
          ContactNumber: settings.contact_number,
          ContactEmail: settings.contact_email,
        },
      ]);
      if (response?.status === 200) {
        toast.success(response.message || "Saved");
        setAuthCode("");
        loadSettings();
      } else {
        toast.error(response?.error || response?.message || "Could not save.");
      }
    } catch (e) {
      toast.error("Could not save.");
    }
    setSavingSettings(false);
  };

  // Uses what is in the form (unsaved edits included); blanks fall back to the saved values.
  const checkDetails = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const response = await companiesHouseFilingSettingsCheckApi([
        {
          CompanyNumber: settings.company_number,
          CompanyName: settings.company_name,
          CompanyType: settings.company_type,
          AuthCode: authCode,
        },
      ]);
      if (response?.status === 200) setCheckResult(response);
      else toast.error(response?.error || response?.message || "Could not check the details.");
    } catch (e) {
      toast.error("Could not check the details.");
    }
    setChecking(false);
  };

  const checkStatus = async (id) => {
    setCheckingId(id);
    try {
      const response = await companiesHouseFilingSubmissionCheckStatusApi(id);
      if (response?.status === 200) {
        toast.info(response.message);
        loadSubmissions();
      } else {
        toast.error(response?.error || response?.message || "Could not check the status.");
      }
    } catch (e) {
      toast.error("Could not check the status.");
    }
    setCheckingId(null);
  };

  // Change #100745 - reopen the filing form pre-filled from a previous submission.
  const startResubmit = (accounts) => {
    setViewing(null);
    setResubmitFrom(accounts);
    setFileOpen(true);
  };

  const resubmit = async (id) => {
    setResubmittingId(id);
    try {
      const response = await companiesHouseFilingSubmissionDataApi(id);
      const accounts = response?.companieshousesubmission_data?.Accounts;
      if (response?.status === 200 && accounts) startResubmit(accounts);
      else toast.error(response?.error || "That submission has no stored figures to reuse.");
    } catch (e) {
      toast.error("Could not load the submission.");
    }
    setResubmittingId(null);
  };

  const openSubmission = async (id) => {
    try {
      const response = await companiesHouseFilingSubmissionDataApi(id);
      if (response?.status === 200) setViewing(response.companieshousesubmission_data);
      else toast.error(response?.error || "Could not load the submission.");
    } catch (e) {
      toast.error("Could not load the submission.");
    }
  };

  if (loading) {
    return (
      <Card className="p-2">
        <LoadingSpinner />
      </Card>
    );
  }

  const liveAvailable = Boolean(meta?.live_available);
  const detailsReady = Boolean(settings.company_number && settings.company_name && meta?.auth_code_set);
  const set = (key) => (e) => setSettings((s) => ({ ...s, [key]: e.target.value }));

  return (
    <Card className="p-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <h5 className="mb-0">Companies House</h5>
        <Badge color={liveAvailable ? "success" : "warning"}>
          {liveAvailable ? `Live filing available (package ${meta.live_package_reference})` : "Test service only"}
        </Badge>
      </div>
      <p className="text-muted mt-2 mb-0">
        File annual accounts with Companies House using CRMit as your filing software. Micro-entity accounts
        are supported. Every filing can be previewed first, and goes to the Companies House test service
        unless you explicitly choose live.
      </p>

      <hr />

      {/* Filing details */}
      <h6 className="mb-3">Filing details</h6>
      <Row className="g-3">
        <Col md={3}>
          <Label className="form-label">Company number</Label>
          <Input value={settings.company_number} onChange={set("company_number")} placeholder="01234567" maxLength={8} />
        </Col>
        <Col md={5}>
          <Label className="form-label">Registered company name</Label>
          <Input value={settings.company_name} onChange={set("company_name")} placeholder="As registered at Companies House" />
        </Col>
        <Col md={4}>
          <Label className="form-label">Registered in</Label>
          <Input type="select" value={settings.company_type} onChange={set("company_type")}>
            {(meta?.company_types || Object.keys(COMPANY_TYPE_LABELS)).map((t) => (
              <option key={t} value={t}>
                {COMPANY_TYPE_LABELS[t] || t}
              </option>
            ))}
          </Input>
        </Col>
        <Col md={3}>
          <Label className="form-label">Authentication code</Label>
          <Input
            type="password"
            autoComplete="new-password"
            value={authCode}
            onChange={(e) => setAuthCode(e.target.value.toUpperCase())}
            placeholder={meta?.auth_code_set ? "Saved - leave blank to keep" : "6 characters"}
            maxLength={6}
          />
          <div className="form-text">The company's code from Companies House, not your WebFiling password.</div>
        </Col>
        <Col md={3}>
          <Label className="form-label">Contact name</Label>
          <Input value={settings.contact_name} onChange={set("contact_name")} />
        </Col>
        <Col md={3}>
          <Label className="form-label">Contact phone</Label>
          <Input value={settings.contact_number} onChange={set("contact_number")} />
        </Col>
        <Col md={3}>
          <Label className="form-label">Contact email</Label>
          <Input type="email" value={settings.contact_email} onChange={set("contact_email")} />
        </Col>
      </Row>
      <div className="mt-3">
        <Button color="primary" onClick={saveSettings} disabled={savingSettings || checking}>
          {savingSettings && <Spinner size="sm" className="me-1" />}
          Save filing details
        </Button>
        <Button color="primary" outline className="ms-2" onClick={checkDetails} disabled={checking || savingSettings || !settings.company_number}>
          {checking ? <Spinner size="sm" className="me-1" /> : <i className="ri-shield-check-line me-1" />}
          Check details
        </Button>
        <span className="text-muted small ms-2">Checks with Companies House without filing anything.</span>
      </div>

      {checkResult && (
        <CheckResults
          result={checkResult}
          onUseName={(name) => setSettings((s) => ({ ...s, company_name: name }))}
          onUseType={(type) => setSettings((s) => ({ ...s, company_type: type }))}
        />
      )}

      <hr />

      {/* Submissions */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <h6 className="mb-0">Accounts filings</h6>
        <Button
          color="primary"
          onClick={() => {
            setResubmitFrom(null);
            setFileOpen(true);
          }}
          disabled={!detailsReady}
        >
          <i className="ri-file-upload-line me-1" />
          File micro-entity accounts
        </Button>
      </div>
      {!detailsReady && (
        <Alert color="info" className="py-2">
          Save the company number, registered name and authentication code above before filing.
        </Alert>
      )}

      {submissions.length === 0 ? (
        <p className="text-muted mb-0">No accounts have been filed from CRMit yet.</p>
      ) : (
        <div className="table-responsive">
          <Table size="sm" className="align-middle mb-0">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Period end</th>
                <th>Service</th>
                <th>Submission</th>
                <th>Transaction ID</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {submissions.map((s) => (
                <tr key={s.Id}>
                  <td>{fmtDate(s.CreatedDateTime, "DD MMM YYYY HH:mm")}</td>
                  <td>{fmtDate(s.PeriodEndDate)}</td>
                  <td>
                    <Badge color={s.Environment === "live" ? "success" : "secondary"}>
                      {s.Environment === "live" ? "Live" : "Test"}
                    </Badge>
                  </td>
                  <td className="font-monospace">{s.SubmissionNumber}</td>
                  <td className="font-monospace small">{s.TransactionId || "-"}</td>
                  <td style={{ maxWidth: 360 }}>
                    <Badge color={STATUS_COLORS[s.StatusCode] || "secondary"} className="me-1">
                      {STATUS_LABELS[s.StatusCode] || s.StatusCode}
                    </Badge>
                    <span className="small text-muted">{s.StatusMessage}</span>
                    {s.LastStatusCheckDateTime && (
                      <div className="small text-muted">Checked {moment(s.LastStatusCheckDateTime).fromNow()}</div>
                    )}
                  </td>
                  <td className="text-end text-nowrap">
                    {!FINAL_STATUSES.includes(s.StatusCode) && (
                      <Button size="sm" color="primary" outline className="me-1" onClick={() => checkStatus(s.Id)} disabled={checkingId === s.Id}>
                        {checkingId === s.Id && <Spinner size="sm" className="me-1" />}
                        Check status
                      </Button>
                    )}
                    <Button size="sm" color="light" className="me-1" onClick={() => openSubmission(s.Id)}>
                      View
                    </Button>
                    <Button size="sm" color="primary" outline onClick={() => resubmit(s.Id)} disabled={!detailsReady || resubmittingId === s.Id}>
                      {resubmittingId === s.Id ? <Spinner size="sm" className="me-1" /> : <i className="ri-refresh-line me-1" />}
                      Resubmit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {fileOpen && (
        <FileAccountsModal
          settings={settings}
          suggest={checkResult?.suggest}
          initial={resubmitFrom}
          liveAvailable={liveAvailable}
          onClose={() => setFileOpen(false)}
          onFiled={() => {
            setFileOpen(false);
            loadSubmissions();
          }}
        />
      )}

      <AppModal
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing ? `Submission ${viewing.SubmissionNumber}` : ""}
        subtitle={viewing ? `${viewing.Environment === "live" ? "Live" : "Test"} service - period ending ${fmtDate(viewing.PeriodEndDate)}` : ""}
        size="lg"
        hideCancel
        cancelLabel="Close"
      >
        {viewing && (
          <>
            <p className="mb-2">
              <Badge color={STATUS_COLORS[viewing.StatusCode] || "secondary"} className="me-1">
                {STATUS_LABELS[viewing.StatusCode] || viewing.StatusCode}
              </Badge>
              {viewing.StatusMessage}
            </p>
            <p className="small text-muted mb-2">
              Submission <span className="font-monospace">{viewing.SubmissionNumber}</span>
              {" - "}Transaction ID <span className="font-monospace">{viewing.TransactionId || "-"}</span>
            </p>
            {(viewing.Rejections || []).length > 0 && (
              <Alert color="danger" className="py-2">
                <strong>Rejection reasons</strong>
                <ul className="mb-0">
                  {viewing.Rejections.map((r, i) => (
                    <li key={i}>
                      {r.code ? <span className="font-monospace me-1">{r.code}</span> : null}
                      {r.description}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
            {viewing.IxbrlData && (
              <>
                <div className="text-end mb-2">
                  {viewing.Accounts && (
                    <Button size="sm" color="primary" outline className="me-1" onClick={() => startResubmit(viewing.Accounts)} disabled={!detailsReady}>
                      <i className="ri-refresh-line me-1" />
                      Resubmit
                    </Button>
                  )}
                  <Button
                    size="sm"
                    color="primary"
                    outline
                    onClick={() => printIxbrl(viewing.IxbrlData, pdfTitle(viewing.CompanyNumber, viewing.PeriodEndDate))}
                  >
                    <i className="ri-file-pdf-line me-1" />
                    Download PDF
                  </Button>
                </div>
                <IxbrlFrame html={viewing.IxbrlData} />
              </>
            )}
          </>
        )}
      </AppModal>
    </Card>
  );
}

const CHECK_ICON = {
  1: { icon: "ri-checkbox-circle-fill", color: "text-success" },
  0: { icon: "ri-close-circle-fill", color: "text-danger" },
  null: { icon: "ri-question-fill", color: "text-warning" },
};

function CheckResults({ result, onUseName, onUseType }) {
  const { checks = [], suggest = {} } = result;
  const allOk = checks.every((c) => c.ok === 1);
  const anyFail = checks.some((c) => c.ok === 0);

  return (
    <Alert color={allOk ? "success" : anyFail ? "danger" : "warning"} className="mt-3 mb-0">
      <div className="fw-semibold mb-2">
        {allOk ? "Everything checks out." : anyFail ? "Some details need fixing." : "Mostly fine - some things could not be confirmed."}
      </div>
      <ul className="list-unstyled mb-0">
        {checks.map((c) => {
          const style = CHECK_ICON[c.ok === null || c.ok === undefined ? "null" : c.ok];
          return (
            <li key={c.key} className="d-flex gap-2 mb-1">
              <i className={`${style.icon} ${style.color}`} />
              <div>
                <strong>{c.label}:</strong> {c.message}
                {c.key === "name" && c.ok === 0 && suggest.company_name && (
                  <Button size="sm" color="link" className="p-0 ms-2 align-baseline" onClick={() => onUseName(suggest.company_name)}>
                    Use registered name
                  </Button>
                )}
                {c.key === "jurisdiction" && c.ok === 0 && suggest.company_type && (
                  <Button size="sm" color="link" className="p-0 ms-2 align-baseline" onClick={() => onUseType(suggest.company_type)}>
                    Use {COMPANY_TYPE_LABELS[suggest.company_type] || suggest.company_type}
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {suggest.period_end && (
        <div className="small mt-2">
          Next accounts: period {fmtDate(suggest.period_start)} to {fmtDate(suggest.period_end)}, due {fmtDate(suggest.accounts_due)}
          {suggest.overdue ? <Badge color="danger" className="ms-1">Overdue</Badge> : null}. These dates are pre-filled when you file.
        </div>
      )}
      <div className="small text-muted mt-2">Remember to save any changes.</div>
    </Alert>
  );
}

function IxbrlFrame({ html }) {
  // sandbox="" - no scripts, no navigation; it is our own generated document but treat it as content.
  return (
    <iframe
      title="Accounts preview"
      sandbox=""
      srcDoc={html}
      style={{ width: "100%", height: 520, border: "1px solid var(--bs-border-color)", borderRadius: 6, background: "#fff" }}
    />
  );
}

// Change #100741 - open the iXBRL in a print window so "Save as PDF" keeps a copy of what was filed.
// The document is ours but still treated as content: scripts are stripped and blocked by a CSP meta.
// The window title becomes the browser's default PDF file name.
function printIxbrl(html, title) {
  const win = window.open("", "_blank");
  if (!win) {
    toast.error("Allow pop-ups for CRMit to download the PDF.");
    return;
  }
  const safe = String(html || "").replace(/<script[\s\S]*?<\/script>/gi, "");
  const csp = `<meta http-equiv="Content-Security-Policy" content="script-src 'none'">`;
  const doc = /<head[^>]*>/i.test(safe) ? safe.replace(/<head[^>]*>/i, (m) => m + csp) : csp + safe;
  win.document.open();
  win.document.write(doc);
  win.document.close();
  win.document.title = title;
  win.focus();
  setTimeout(() => win.print(), 300);
}

const pdfTitle = (companyNumber, periodEnd) =>
  `Accounts ${companyNumber || ""} ${periodEnd ? moment(periodEnd).format("YYYY-MM-DD") : ""}`.trim();

// Change #100745 - the reverse of yearPayload(), to refill the form from a stored submission's Accounts.
const str = (v) => (v === null || v === undefined ? "" : String(v));
const yearFromPayload = (y) => {
  const bs = y?.BalanceSheet || {};
  return {
    CalledUpShareCapitalNotPaid: str(bs.CalledUpShareCapitalNotPaid),
    FixedAssets: str(bs.FixedAssets),
    CurrentAssets: str(bs.CurrentAssets?.Total),
    CreditorsWithinOneYear: str(bs.Liabilities?.CreditorsWithinOneYear),
    CreditorsAfterOneYear: str(bs.Liabilities?.CreditorsAfterOneYear),
    Equity: str(bs.Equity?.Total),
    AverageNumberEmployees: str(y?.AverageNumberEmployees),
  };
};

function FileAccountsModal({ settings, suggest, initial, liveAvailable, onClose, onFiled }) {
  // Pre-filled from a previous submission (Resubmit, #100745) when given, otherwise from
  // "Check details" (the register's next accounts period) when it has been run.
  const director = initial?.Directors?.[0] || {};
  const [periodStart, setPeriodStart] = useState(initial?.PeriodStartDate || suggest?.period_start || "");
  const [periodEnd, setPeriodEnd] = useState(initial?.BalanceSheetDate || suggest?.period_end || "");
  const [current, setCurrent] = useState(initial ? yearFromPayload(initial) : emptyYear());
  const [previous, setPrevious] = useState(initial?.PreviousYear ? yearFromPayload(initial.PreviousYear) : emptyYear());
  const [firstAccounts, setFirstAccounts] = useState(initial ? !initial.PreviousYear : Boolean(suggest?.first_accounts));
  const [directorName, setDirectorName] = useState(director.Name || "");
  const [approvalDate, setApprovalDate] = useState(director.ApprovalDate || moment().format("YYYY-MM-DD"));
  // A stored submission was only ever sent with the statements confirmed.
  const [statements, setStatements] = useState(Boolean(initial));
  // Change #100743 - note 1 "Statutory information"; both come from the register via "Check details".
  const [registeredOffice, setRegisteredOffice] = useState(initial?.RegisteredOffice || suggest?.registered_office || "");
  const companyKind = initial?.CompanyKind || suggest?.company_kind || "";

  const [step, setStep] = useState("form"); // form | preview
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(false);
  const [confirmNumber, setConfirmNumber] = useState("");

  const accounts = useMemo(
    () => ({
      PeriodStartDate: periodStart,
      BalanceSheetDate: periodEnd,
      Currency: "GBP",
      RegisteredOffice: registeredOffice,
      CompanyKind: companyKind,
      ...yearPayload(current),
      ...(firstAccounts ? {} : { PreviousYear: yearPayload(previous) }),
      Directors: [{ Name: directorName, ApprovalDate: approvalDate }],
      Statements: {
        AuditExemption: true,
        MembersHaveNotRequiredAudit: true,
        DirectorsAcknowledgeResponsibilities: true,
        MicroEntityProvisions: true,
      },
    }),
    [periodStart, periodEnd, registeredOffice, companyKind, current, previous, firstAccounts, directorName, approvalDate]
  );

  const formError = useMemo(() => {
    if (!periodStart || !periodEnd) return "Enter the accounting period.";
    if (moment(periodEnd).isSameOrBefore(moment(periodStart))) return "The balance sheet date must be after the period start.";
    if (!directorName.trim()) return "Enter the director approving the accounts.";
    if (!approvalDate) return "Enter the date the accounts were approved.";
    if (!statements) return "Confirm the micro-entity statements.";
    return null;
  }, [periodStart, periodEnd, directorName, approvalDate, statements]);

  const doPreview = async () => {
    setBusy(true);
    try {
      const response = await companiesHouseFilingPreviewApi([{ Accounts: accounts }]);
      if (response?.status === 200) {
        setPreview(response);
        setStep("preview");
      } else {
        toast.error(response?.error || "Could not build the preview.");
      }
    } catch (e) {
      toast.error("Could not build the preview.");
    }
    setBusy(false);
  };

  const doSubmit = async () => {
    setBusy(true);
    try {
      const response = await companiesHouseFilingSubmitApi([
        { Accounts: accounts, Live: live, ConfirmCompanyNumber: live ? confirmNumber : "" },
      ]);
      if (response?.status === 200) {
        toast.success(`${response.environment === "live" ? "Filed with Companies House" : "Sent to the Companies House test service"} - submission ${response.submission?.SubmissionNumber}`);
        onFiled();
        return;
      }
      toast.error(response?.error || response?.message || "The filing was not accepted by the gateway.");
      if (response?.submission) onFiled(); // an ERROR row was logged - show it in the history
    } catch (e) {
      toast.error("Could not reach CRMit to file the accounts.");
    }
    setBusy(false);
  };

  const yearInput = (state, setState, key) => (
    <Input
      type="number"
      step="1"
      value={state[key]}
      onChange={(e) => setState((s) => ({ ...s, [key]: e.target.value }))}
      className="text-end"
    />
  );

  const liveConfirmed = !live || confirmNumber.trim().toUpperCase() === settings.company_number;

  return (
    <AppModal
      open
      onClose={onClose}
      title="File micro-entity accounts"
      subtitle={`${settings.company_name} (${settings.company_number})`}
      size="lg"
      loading={busy}
      footer={
        step === "form" ? (
          <>
            <Button color="light" onClick={onClose}>
              Cancel
            </Button>
            <Button color="primary" onClick={doPreview} disabled={Boolean(formError) || busy}>
              Preview accounts
            </Button>
          </>
        ) : (
          <>
            <Button color="light" onClick={() => setStep("form")} disabled={busy}>
              Back
            </Button>
            <Button
              color="primary"
              outline
              onClick={() => printIxbrl(preview?.ixbrl_data, pdfTitle(settings.company_number, periodEnd))}
              disabled={busy || !preview?.ixbrl_data}
            >
              <i className="ri-file-pdf-line me-1" />
              Download PDF
            </Button>
            <Button color={live ? "danger" : "primary"} onClick={doSubmit} disabled={busy || !liveConfirmed || !preview?.valid}>
              {live ? "File live with Companies House" : "Send to test service"}
            </Button>
          </>
        )
      }
    >
      {step === "form" ? (
        <>
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Label className="form-label">Period start</Label>
              <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
            </Col>
            <Col md={4}>
              <Label className="form-label">Balance sheet date (period end)</Label>
              <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <FormGroup check className="mb-2">
                <Input type="checkbox" id="ch-first" checked={firstAccounts} onChange={(e) => setFirstAccounts(e.target.checked)} />
                <Label check for="ch-first">
                  First accounts (no prior year)
                </Label>
              </FormGroup>
            </Col>
          </Row>

          <Table size="sm" className="align-middle">
            <thead>
              <tr>
                <th>Balance sheet (£)</th>
                <th className="text-end" style={{ width: 160 }}>
                  This year
                </th>
                {!firstAccounts && (
                  <th className="text-end" style={{ width: 160 }}>
                    Prior year
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {BALANCE_SHEET_LINES.map(([key, label]) => (
                <tr key={key}>
                  <td>{label}</td>
                  <td>{yearInput(current, setCurrent, key)}</td>
                  {!firstAccounts && <td>{yearInput(previous, setPrevious, key)}</td>}
                </tr>
              ))}
              <tr className="fw-semibold">
                <td>Net assets (calculated)</td>
                <td className="text-end">{netAssets(current).toLocaleString()}</td>
                {!firstAccounts && <td className="text-end">{netAssets(previous).toLocaleString()}</td>}
              </tr>
              <tr>
                <td>Capital and reserves</td>
                <td>{yearInput(current, setCurrent, "Equity")}</td>
                {!firstAccounts && <td>{yearInput(previous, setPrevious, "Equity")}</td>}
              </tr>
              <tr>
                <td>Average number of employees</td>
                <td>{yearInput(current, setCurrent, "AverageNumberEmployees")}</td>
                {!firstAccounts && <td>{yearInput(previous, setPrevious, "AverageNumberEmployees")}</td>}
              </tr>
            </tbody>
          </Table>
          {Math.abs(netAssets(current) - num(current.Equity)) > 0.5 && (
            <Alert color="warning" className="py-2">
              Net assets and capital and reserves do not match - the balance sheet does not balance.
            </Alert>
          )}

          <Row className="g-3 mb-3">
            <Col md={6}>
              <Label className="form-label">Director approving the accounts</Label>
              <Input value={directorName} onChange={(e) => setDirectorName(e.target.value)} />
            </Col>
            <Col md={6}>
              <Label className="form-label">Date approved by the board</Label>
              <Input type="date" value={approvalDate} onChange={(e) => setApprovalDate(e.target.value)} />
            </Col>
            <Col md={12}>
              <Label className="form-label">Registered office</Label>
              <Input
                value={registeredOffice}
                onChange={(e) => setRegisteredOffice(e.target.value)}
                placeholder="Shown in note 1 - run Check details to fill it from the register"
              />
            </Col>
          </Row>

          <FormGroup check>
            <Input type="checkbox" id="ch-statements" checked={statements} onChange={(e) => setStatements(e.target.checked)} />
            <Label check for="ch-statements" className="small">
              The directors confirm: the company was entitled to exemption from audit under section 477 of the
              Companies Act 2006; the members have not required an audit under section 476; the directors
              acknowledge their responsibilities for complying with the Act; and the accounts have been prepared
              in accordance with the micro-entity provisions.
            </Label>
          </FormGroup>
          {formError && <div className="text-muted small mt-2">{formError}</div>}
        </>
      ) : (
        <>
          {!preview?.valid && (
            <Alert color="danger" className="py-2">
              The generated accounts are malformed and cannot be sent: {(preview?.xml_errors || []).join("; ")}
            </Alert>
          )}
          {(preview?.warnings || []).length > 0 && (
            <Alert color="warning" className="py-2">
              <ul className="mb-0">
                {preview.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </Alert>
          )}
          <IxbrlFrame html={preview?.ixbrl_data || ""} />

          <div className="mt-3">
            <Label className="form-label d-block">Send to</Label>
            <FormGroup check inline>
              <Input type="radio" id="ch-test" checked={!live} onChange={() => setLive(false)} />
              <Label check for="ch-test">
                Companies House test service
              </Label>
            </FormGroup>
            <FormGroup check inline>
              <Input type="radio" id="ch-live" checked={live} onChange={() => setLive(true)} disabled={!liveAvailable} />
              <Label check for="ch-live">
                Live - public register{!liveAvailable ? " (not enabled on this server)" : ""}
              </Label>
            </FormGroup>
          </div>
          {live && (
            <Alert color="danger" className="mt-2 mb-0">
              A live filing goes on the public register and cannot be withdrawn. Type the company number{" "}
              <strong>{settings.company_number}</strong> to confirm.
              <Input className="mt-2" value={confirmNumber} onChange={(e) => setConfirmNumber(e.target.value)} placeholder={settings.company_number} />
            </Alert>
          )}
        </>
      )}
    </AppModal>
  );
}
